import React, { useEffect } from 'react'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTheme } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import type { MuiButtonProps } from 'components/Template/Buttons/MuiButton'
import { RoundedButton } from 'components/Template/Buttons/RoundedButtons'
import Typography from 'components/Template/MuiTypography'
import useAudioProperties from 'hooks/useAudioProperties'
import paths from 'paths.json'
import { EPISODE_DETAIL_QUERY_KEY, fetchEpisodeDetail } from 'queries'
import { track } from 'utils/monitoring'
import { redirectWithQueryString } from 'utils/ts-misc'

interface EpisodeDetailPageButtonProps {
    color?: MuiButtonProps['color']
    contentType?: 'follow_up' | 'moment'
    episodeSlug: string
    hashid?: string
    label: string
    pageName: string
}

export default function EpisodeDetailPageButton({
    color,
    contentType,
    episodeSlug,
    hashid,
    label,
    pageName
}: EpisodeDetailPageButtonProps) {
    const { episodeSlug: audioEpisodeSlug, resetAudioProperties } = useAudioProperties()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    // Make potential navigation to the corresponding listen page happen instantly.
    useEffect(() => {
        queryClient.prefetchQuery(EPISODE_DETAIL_QUERY_KEY(episodeSlug), () => fetchEpisodeDetail(episodeSlug))
    }, [])

    const theme = useTheme()

    const handleClick = () => {
        // Keep the audio player on when navigating from the listen page to the *corresponding* transcript page.
        if (audioEpisodeSlug !== episodeSlug) resetAudioProperties()

        // For now, you can assume the only entrypoints are the moment detail & transcript pages.
        const properties: Record<string, string> = {
            episode_slug: episodeSlug,
            label: label.toLowerCase().replace(' ', '_'),
            location: window.location.pathname,
            page: pageName
        }
        if (contentType && hashid) properties[`${contentType}_hashid`] = hashid
        track('Button Clicked', properties)
        // Navigate to the listening page and preserve the query parameters (?email=) to make sure users coming from
        // emails won't see the authentication page on unlisted episodes.
        navigate(redirectWithQueryString(paths.episodeDetail.replace(':episodeSlug', episodeSlug)))
    }

    return (
        <RoundedButton
            color={color}
            endIcon={<ArrowForwardIcon color='inherit' />}
            onClick={handleClick}
            style={{ padding: theme.spacing(1, 3) }}
            variant='contained'
        >
            <Typography variant={'subtitle2'}>{label}</Typography>
        </RoundedButton>
    )
}
